/**
 * Users.
 *
 * The screen an administrator uses to answer three questions: who can sign in,
 * what may they do, and where may they do it. Everything it writes goes
 * through the published operations, so the rules the server enforces are the
 * rules the screen shows — it never pretends an edit succeeded that the server
 * would refuse.
 *
 * A password is typed here and kept nowhere: not in a query cache, not in a
 * form that outlives its dialog.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { KeyRound, Pencil, Plus } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { surfaceProblem } from "@/components/form";
import { ApiError } from "@/lib/api/errors";
import { localizedName } from "@/lib/i18n";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  createUser,
  getUser,
  replaceUserBranches,
  replaceUserRoles,
  setUserPassword,
  updateUser,
  type User,
  type UserCreate,
} from "../api";
import { identityKeys, useRoles, useUsers } from "../queries";

export interface Branch {
  id: string;
  code: string;
  name: string;
  name_ar?: string | null;
}

const MINIMUM_PASSWORD = 12;

interface UserDraft {
  code: string;
  displayName: string;
  password: string;
  defaultBranchId: string;
  branchIds: string[];
  roleIds: string[];
}

const EMPTY_DRAFT: UserDraft = {
  code: "",
  displayName: "",
  password: "",
  defaultBranchId: "",
  branchIds: [],
  roleIds: [],
};

const NO_BRANCH = "__none__";

export interface UsersPageProps {
  branches: Branch[];
  canCreate?: boolean;
  canUpdate?: boolean;
  canSetPassword?: boolean;
}

export function UsersPage({
  branches,
  canCreate = true,
  canUpdate = true,
  canSetPassword = true,
}: UsersPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const usersQuery = useUsers();
  const rolesQuery = useRoles();
  const users = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);
  const roles = useMemo(() => rolesQuery.data ?? [], [rolesQuery.data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<{ user: User; etag: string | null } | null>(null);
  const [draft, setDraft] = useState<UserDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<Partial<Record<keyof UserDraft, string>>>({});
  const [passwordTarget, setPasswordTarget] = useState<{ user: User; etag: string | null } | null>(
    null,
  );
  const [newPassword, setNewPassword] = useState("");

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: identityKeys.users.all });
  };

  const roleName = (roleId: string): string => {
    const role = roles.find((candidate) => candidate.id === roleId);
    return role === undefined ? roleId : localizedName(role.name, role.name_ar, i18n.language);
  };

  const branchName = (branchId: string): string => {
    const branch = branches.find((candidate) => candidate.id === branchId);
    return branch === undefined
      ? branchId
      : `${branch.code} — ${localizedName(branch.name, branch.name_ar, i18n.language)}`;
  };

  const openCreate = (): void => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setErrors({});
    setDialogOpen(true);
  };

  const loadForEdit = useMutation({
    mutationFn: (userId: string) => getUser(userId),
    onSuccess: ({ data, etag }) => {
      setEditing({ user: data, etag });
      setDraft({
        code: data.code,
        displayName: data.display_name,
        password: "",
        defaultBranchId: data.default_branch_id ?? "",
        branchIds: [...data.allowed_branch_ids],
        roleIds: [...data.role_ids],
      });
      setErrors({});
      setDialogOpen(true);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const loadForPassword = useMutation({
    mutationFn: (userId: string) => getUser(userId),
    onSuccess: ({ data, etag }) => {
      setPasswordTarget({ user: data, etag });
      setNewPassword("");
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const onWriteError = (error: unknown): void => {
    if (error instanceof ApiError && error.status === 409) {
      setErrors({ code: error.problem?.detail ?? t("identity.users.codeTaken") });
      return;
    }
    surfaceProblem(error, t);
  };

  const createMutation = useIdempotentMutation<User, UserCreate>({
    mutationFn: (input, key) => createUser(input, key),
    onSuccess: () => {
      setDialogOpen(false);
      setDraft(EMPTY_DRAFT);
      invalidate();
    },
    onError: onWriteError,
  });

  /**
   * An edit can touch three independent resources, and each one moves the
   * validator. They are applied in sequence with the ETag the previous step
   * returned, so a partial failure stops rather than writing with a stale one.
   */
  const saveMutation = useMutation({
    mutationFn: async (input: { user: User; etag: string; draft: UserDraft }) => {
      let etag = input.etag;
      const changedName = input.draft.displayName.trim() !== input.user.display_name;
      const changedDefault = input.draft.defaultBranchId !== (input.user.default_branch_id ?? "");
      const changedBranches = !sameSet(input.draft.branchIds, input.user.allowed_branch_ids);
      const changedRoles = !sameSet(input.draft.roleIds, input.user.role_ids);

      // Branches first: a default may only name a branch already assigned.
      if (changedBranches) {
        const result = await replaceUserBranches(input.user.id, input.draft.branchIds, etag);
        etag = result.etag ?? etag;
      }
      if (changedRoles) {
        const result = await replaceUserRoles(input.user.id, input.draft.roleIds, etag);
        etag = result.etag ?? etag;
      }
      if (changedName || changedDefault) {
        await updateUser(
          input.user.id,
          {
            ...(changedName ? { display_name: input.draft.displayName.trim() } : {}),
            ...(changedDefault
              ? {
                  default_branch_id:
                    input.draft.defaultBranchId === "" ? null : input.draft.defaultBranchId,
                }
              : {}),
          },
          etag,
        );
      }
    },
    onSuccess: () => {
      setDialogOpen(false);
      invalidate();
    },
    onError: onWriteError,
  });

  const statusMutation = useMutation({
    mutationFn: (input: { user: User; etag: string; status: "active" | "disabled" }) =>
      updateUser(input.user.id, { status: input.status }, input.etag),
    onSuccess: invalidate,
    onError: (error) => surfaceProblem(error, t),
  });

  const passwordMutation = useMutation({
    mutationFn: (input: { userId: string; etag: string; password: string }) =>
      setUserPassword(input.userId, input.password, input.etag),
    onSuccess: () => {
      // The password is dropped the moment the request that carried it returns.
      setPasswordTarget(null);
      setNewPassword("");
      invalidate();
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const validate = (): boolean => {
    const next: Partial<Record<keyof UserDraft, string>> = {};
    if (editing === null && draft.code.trim() === "") next.code = t("identity.users.codeRequired");
    if (draft.displayName.trim() === "") next.displayName = t("identity.users.nameRequired");
    if (editing === null && draft.password.length < MINIMUM_PASSWORD) {
      next.password = t("identity.users.passwordTooShort", { count: MINIMUM_PASSWORD });
    }
    if (draft.defaultBranchId !== "" && !draft.branchIds.includes(draft.defaultBranchId)) {
      next.defaultBranchId = t("identity.users.defaultMustBeAssigned");
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = (): void => {
    if (!validate()) return;
    if (editing === null) {
      createMutation.mutate({
        code: draft.code.trim(),
        display_name: draft.displayName.trim(),
        password: draft.password,
        ...(draft.defaultBranchId === "" ? {} : { default_branch_id: draft.defaultBranchId }),
        allowed_branch_ids: draft.branchIds,
        role_ids: draft.roleIds,
      });
      return;
    }
    if (editing.etag === null) {
      surfaceProblem(new Error(t("errors.generic")), t);
      return;
    }
    saveMutation.mutate({ user: editing.user, etag: editing.etag, draft });
  };

  const saving = createMutation.isPending || saveMutation.isPending;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="flex-1 text-2xl font-semibold tracking-tight">
          {t("identity.users.title")}
        </h1>
        {canCreate ? (
          <Button onClick={openCreate}>
            <Plus className="me-2 size-4" aria-hidden="true" />
            {t("identity.users.new")}
          </Button>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("identity.users.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {usersQuery.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {t("errors.generic")}
            </p>
          ) : usersQuery.isPending ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : users.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("table.empty")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-start text-xs font-medium text-muted-foreground">
                    <th className="px-4 py-2.5 font-medium">{t("identity.users.code")}</th>
                    <th className="px-4 py-2.5 font-medium">{t("identity.users.name")}</th>
                    <th className="px-4 py-2.5 font-medium">{t("identity.users.roles")}</th>
                    <th className="px-4 py-2.5 font-medium">{t("identity.users.branches")}</th>
                    <th className="px-4 py-2.5 font-medium">{t("identity.users.status")}</th>
                    <th className="px-4 py-2.5 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={user.id}
                      className="border-b transition-colors last:border-0 hover:bg-muted/40"
                    >
                      <td className="px-4 py-3 align-top font-mono text-xs">{user.code}</td>
                      <td className="px-4 py-3 align-top">{user.display_name}</td>
                      <td className="px-4 py-3 align-top">
                        {user.role_ids.length === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <span className="flex flex-wrap gap-1">
                            {user.role_ids.map((roleId) => (
                              <Badge key={roleId} variant="secondary">
                                {roleName(roleId)}
                              </Badge>
                            ))}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-muted-foreground">
                        {user.allowed_branch_ids.length}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className="flex flex-wrap items-center gap-1">
                          <Badge variant={user.status === "active" ? "default" : "secondary"}>
                            {t(`status.${user.status}`, user.status)}
                          </Badge>
                          {user.locked === true ? (
                            <Badge variant="destructive">{t("identity.users.locked")}</Badge>
                          ) : null}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-end">
                        <span className="flex justify-end gap-1">
                          {canSetPassword ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => loadForPassword.mutate(user.id)}
                              aria-label={`${t("identity.users.setPassword")} ${user.code}`}
                            >
                              <KeyRound className="size-4" aria-hidden="true" />
                            </Button>
                          ) : null}
                          {canUpdate ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => loadForEdit.mutate(user.id)}
                              aria-label={`${t("identity.users.edit")} ${user.code}`}
                            >
                              <Pencil className="size-4" aria-hidden="true" />
                            </Button>
                          ) : null}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing === null ? t("identity.users.new") : t("identity.users.edit")}
            </DialogTitle>
            <DialogDescription>{t("identity.users.formHint")}</DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <Field label={t("identity.users.code")} error={errors.code} htmlFor="user-code">
              <Input
                id="user-code"
                dir="ltr"
                // A code identifies a person to everyone who works with them,
                // so it is fixed once the account exists.
                disabled={editing !== null}
                value={draft.code}
                onChange={(event) => setDraft({ ...draft, code: event.target.value })}
              />
            </Field>

            <Field label={t("identity.users.name")} error={errors.displayName} htmlFor="user-name">
              <Input
                id="user-name"
                value={draft.displayName}
                onChange={(event) => setDraft({ ...draft, displayName: event.target.value })}
              />
            </Field>

            {editing === null ? (
              <Field
                label={t("identity.users.password")}
                error={errors.password}
                htmlFor="user-password"
                hint={t("identity.users.passwordHint", { count: MINIMUM_PASSWORD })}
              >
                <Input
                  id="user-password"
                  type="password"
                  dir="ltr"
                  autoComplete="new-password"
                  value={draft.password}
                  onChange={(event) => setDraft({ ...draft, password: event.target.value })}
                />
              </Field>
            ) : null}

            <fieldset className="flex flex-col gap-2">
              <legend className="pb-1 text-sm font-medium">{t("identity.users.branches")}</legend>
              {branches.map((branch) => (
                <label key={branch.id} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={draft.branchIds.includes(branch.id)}
                    onCheckedChange={(value) =>
                      setDraft((current) => ({
                        ...current,
                        branchIds:
                          value === true
                            ? [...current.branchIds, branch.id]
                            : current.branchIds.filter((id) => id !== branch.id),
                      }))
                    }
                  />
                  {branchName(branch.id)}
                </label>
              ))}
            </fieldset>

            <Field
              label={t("identity.users.defaultBranch")}
              error={errors.defaultBranchId}
              htmlFor="user-default-branch"
              hint={t("identity.users.defaultBranchHint")}
            >
              <Select
                value={draft.defaultBranchId === "" ? NO_BRANCH : draft.defaultBranchId}
                onValueChange={(value) =>
                  setDraft({ ...draft, defaultBranchId: value === NO_BRANCH ? "" : value })
                }
              >
                <SelectTrigger id="user-default-branch">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_BRANCH}>{t("identity.users.noDefaultBranch")}</SelectItem>
                  {branches
                    .filter((branch) => draft.branchIds.includes(branch.id))
                    .map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branchName(branch.id)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </Field>

            <fieldset className="flex flex-col gap-2">
              <legend className="pb-1 text-sm font-medium">{t("identity.users.roles")}</legend>
              {roles.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("identity.users.noRolesYet")}</p>
              ) : (
                roles
                  .filter((role) => role.status === "active")
                  .map((role) => (
                    <label key={role.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={draft.roleIds.includes(role.id)}
                        onCheckedChange={(value) =>
                          setDraft((current) => ({
                            ...current,
                            roleIds:
                              value === true
                                ? [...current.roleIds, role.id]
                                : current.roleIds.filter((id) => id !== role.id),
                          }))
                        }
                      />
                      {localizedName(role.name, role.name_ar, i18n.language)}
                    </label>
                  ))
              )}
            </fieldset>

            {editing !== null ? (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={editing.user.status === "active"}
                  onCheckedChange={(value) => {
                    if (editing.etag === null) return;
                    statusMutation.mutate({
                      user: editing.user,
                      etag: editing.etag,
                      status: value === true ? "active" : "disabled",
                    });
                    setDialogOpen(false);
                  }}
                />
                {t("identity.users.activeLabel")}
              </label>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t("actions.cancel")}
              </Button>
              <Button type="submit" disabled={saving}>
                {t("actions.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={passwordTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPasswordTarget(null);
            setNewPassword("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("identity.users.setPassword")}</DialogTitle>
            <DialogDescription>
              {t("identity.users.setPasswordHint", { code: passwordTarget?.user.code ?? "" })}
            </DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (passwordTarget === null || passwordTarget.etag === null) return;
              if (newPassword.length < MINIMUM_PASSWORD) return;
              passwordMutation.mutate({
                userId: passwordTarget.user.id,
                etag: passwordTarget.etag,
                password: newPassword,
              });
            }}
          >
            <Field
              label={t("identity.users.password")}
              htmlFor="reset-password"
              hint={t("identity.users.passwordHint", { count: MINIMUM_PASSWORD })}
            >
              <Input
                id="reset-password"
                type="password"
                dir="ltr"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPasswordTarget(null)}>
                {t("actions.cancel")}
              </Button>
              <Button type="submit" disabled={passwordMutation.isPending}>
                {t("actions.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function sameSet(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const seen = new Set(right);
  return left.every((value) => seen.has(value));
}

function Field({
  label,
  error,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint !== undefined && error === undefined ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
      {error !== undefined ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
